import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, Loader2 } from "lucide-react";
import api, { formatCAD, resolveImg } from "@/lib/api";

export const SearchSuggest = ({
  className = "",
  inputClassName = "w-36",
  formClassName = "rounded-full border border-border bg-card px-3 py-1.5",
  dropdownClassName = "right-0 w-80 max-w-[85vw]",
  placeholder = "Search the mart…",
  onNavigate,
  autoFocus = false,
  testId = "search-input",
}) => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const term = q.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get("/products", { params: { search: term, limit: 6, sort: "featured" } });
        setResults(r.data);
        setOpen(true);
        setActive(-1);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const goToSearch = () => {
    const term = q.trim();
    if (!term) return;
    setOpen(false);
    onNavigate?.();
    navigate(`/shop?search=${encodeURIComponent(term)}`);
  };

  const pick = (p) => {
    setOpen(false);
    setQ("");
    onNavigate?.();
    navigate(`/product/${p.id}`);
  };

  const onKeyDown = (e) => {
    if (!open || !results.length) {
      if (e.key === "Enter") { e.preventDefault(); goToSearch(); }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, -1)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (active >= 0 && results[active]) pick(results[active]);
      else goToSearch();
    } else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <form
        onSubmit={(e) => { e.preventDefault(); goToSearch(); }}
        className={`flex items-center ${formClassName}`}
      >
        <Search className="h-4 w-4 text-muted-foreground" />
        <input
          data-testid={testId}
          value={q}
          autoFocus={autoFocus}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => results.length && setOpen(true)}
          placeholder={placeholder}
          className={`${inputClassName} bg-transparent px-2 text-sm outline-none placeholder:text-muted-foreground`}
        />
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-clay" />}
      </form>

      <AnimatePresence>
        {open && q.trim().length >= 2 && (
          <motion.div
            data-testid="search-suggestions"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.15 }}
            className={`absolute ${dropdownClassName} z-50 mt-2 overflow-hidden rounded-2xl border border-border bg-card shadow-xl`}
          >
            {results.length === 0 ? (
              <div className="px-4 py-5 text-center text-sm text-muted-foreground" data-testid="search-no-results">
                {loading ? "Searching…" : `No products match “${q.trim()}”`}
              </div>
            ) : (
              <ul className="max-h-[70vh] overflow-y-auto py-1.5">
                {results.map((p, i) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      data-testid={`search-suggestion-${p.id}`}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => pick(p)}
                      className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                        active === i ? "bg-sand" : "hover:bg-sand/60"
                      }`}
                    >
                      <img
                        src={resolveImg(p.image_url)}
                        alt={p.name}
                        className="h-11 w-11 shrink-0 rounded-lg border border-border object-cover"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-forest">{p.name}</span>
                        {p.category && <span className="block truncate text-xs text-muted-foreground">{p.category}</span>}
                      </span>
                      <span className="shrink-0 text-sm font-semibold text-clay">{formatCAD(p.price_cents)}</span>
                    </button>
                  </li>
                ))}
                <li>
                  <button
                    type="button"
                    data-testid="search-see-all"
                    onClick={goToSearch}
                    className="mt-1 w-full border-t border-border px-4 py-2.5 text-center text-sm font-medium text-clay hover:bg-sand/60"
                  >
                    See all results for “{q.trim()}”
                  </button>
                </li>
              </ul>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
